import React, { useState } from 'react';
import { Button } from './ui/button';
import { ThumbsUp, ThumbsDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import FeedbackModal from './FeedbackModal';

const RatingButtons = ({ messageId, onRatingSubmitted, isRated = false, userRating = null }) => {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [pendingRating, setPendingRating] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRatingClick = (rating) => {
    setPendingRating(rating);
    setShowFeedbackModal(true);
  };

  const handleFeedbackSubmit = async (feedback) => {
    if (!feedback || feedback.trim().length === 0) {
      toast.error('Feedback is required');
      return;
    }

    if (feedback.length > 300) {
      toast.error('Feedback must be 300 characters or less');
      return;
    }

    setIsSubmitting(true);
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
      const response = await fetch(`${API_BASE}/ratings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          response_id: messageId,
          rating: pendingRating,
          feedback: feedback.trim()
        })
      });

      if (response.status === 409) {
        toast.error('You have already rated this response');
        return;
      }

      if (!response.ok) {
        const error = await response.json();
        toast.error(`Error: ${error.detail}`);
        return;
      }

      const result = await response.json();
      toast.success('Rating submitted successfully!');
      
      // Notify parent component with message ID
      if (onRatingSubmitted) {
        onRatingSubmitted({
          ...result,
          response_id: messageId // Add the message ID to the response
        });
      }
      
      setShowFeedbackModal(false);
    } catch (error) {
      console.error('Failed to submit rating:', error);
      toast.error('Failed to submit rating. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFeedbackCancel = () => {
    setShowFeedbackModal(false);
    setPendingRating(null);
  };

  // Don't show buttons if already rated
  if (isRated) {
    return (
      <div className="flex items-center space-x-2 text-sm text-emerald-600">
        <Check className="w-4 h-4" />
        <span>Rated</span>
        {userRating && (
          <span className="text-slate-500">
            ({userRating === 'thumbs_up' ? '👍' : '👎'})
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center space-x-2 mt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleRatingClick('thumbs_up')}
          disabled={isSubmitting}
          className="flex items-center space-x-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
        >
          <ThumbsUp className="w-4 h-4" />
          <span>Thumbs Up</span>
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleRatingClick('thumbs_down')}
          disabled={isSubmitting}
          className="flex items-center space-x-1 text-red-600 border-red-200 hover:bg-red-50"
        >
          <ThumbsDown className="w-4 h-4" />
          <span>Thumbs Down</span>
        </Button>
      </div>

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={handleFeedbackCancel}
        onSubmit={handleFeedbackSubmit}
        rating={pendingRating}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default RatingButtons;
